import { X, Plane, Globe2, Users, TrendingUp, Sparkles } from 'lucide-react';

interface WelcomeModalProps {
  onClose: () => void;
  onOpenHelp: () => void;
}

export function WelcomeModal({ onClose, onOpenHelp }: WelcomeModalProps) {
  const handleGetStarted = () => {
    onClose();
    onOpenHelp();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative p-8 pb-6 text-center border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 btn-ghost p-2"
            title="Close welcome message"
            aria-label="Close welcome message"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 animate-pulse bg-primary-500/30 rounded-full blur-xl"></div>
              <div className="relative bg-gradient-to-br from-primary-500 to-blue-600 p-4 rounded-full">
                <Plane className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-2">
            Welcome to Personal Flight Tracker!
          </h1>
          <p className="text-white/70 text-lg">
            Track, visualize, and share your flight journeys on an interactive 3D globe
          </p>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6">
          {/* What is this? */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary-500" />
              <h2 className="text-xl font-semibold">What is Personal Flight Tracker?</h2>
            </div>
            <p className="text-white/70">
              Personal Flight Tracker is an interactive application that lets you record all your flights 
              and visualize them on a stunning 3D globe. See your travel history come to life with flight paths 
              connecting the destinations you've visited around the world.
            </p>
          </section>

          {/* Key Features */}
          <section>
            <h2 className="text-xl font-semibold mb-4">Key Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <Globe2 className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Interactive 3D Globe</h3>
                    <p className="text-sm text-white/60">
                      Visualize your flights on a rotating 3D globe with smooth animations
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <Plane className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Flight Tracking</h3>
                    <p className="text-sm text-white/60">
                      Easily add flights with airport search, dates, and round-trip options
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <TrendingUp className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Travel Statistics</h3>
                    <p className="text-sm text-white/60">
                      Track total flights, distance traveled, and countries visited
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <Users className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Friends & Sharing</h3>
                    <p className="text-sm text-white/60">
                      Connect with friends, compare stats, and view each other's globes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How to Use */}
          <section>
            <h2 className="text-xl font-semibold mb-3">How to Use</h2>
            <div className="glass rounded-xl p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-500">
                  1
                </div>
                <div>
                  <p className="text-white/80"><strong>Add Your Flights</strong> - Use the form on the left to add flights by searching for airports</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-500">
                  2
                </div>
                <div>
                  <p className="text-white/80"><strong>Watch Your Globe Grow</strong> - See your flights appear as arcs connecting cities</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-500">
                  3
                </div>
                <div>
                  <p className="text-white/80"><strong>Connect with Friends</strong> - Add friends to compare stats and view their travel journeys</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-500">
                  4
                </div>
                <div>
                  <p className="text-white/80"><strong>Explore & Share</strong> - Interact with the globe and share your travel achievements</p>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Tips */}
          <section className="glass rounded-xl p-5 bg-primary-500/10">
            <h3 className="text-lg font-semibold mb-3">💡 Quick Tips</h3>
            <ul className="space-y-2 text-sm text-white/70">
              <li>✈️ Start by adding a few flights to see them on the globe</li>
              <li>🌍 Drag the globe to rotate, scroll to zoom in/out</li>
              <li>📱 On mobile, tap the menu icon to access flight controls</li>
              <li>👥 Set up your profile before adding friends</li>
              <li>📚 Check the Help section for detailed guides on all features</li>
            </ul>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="p-6 pt-4 border-t border-white/10 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-primary px-6 py-3"
          >
            Start Exploring
          </button>
          <button
            onClick={handleGetStarted}
            className="btn-secondary px-6 py-3"
          >
            View User Guides
          </button>
        </div>
      </div>
    </div>
  );
}
